import React, { useState, useMemo, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  styled,
  useTheme,
  createTheme,
  ThemeProvider,
} from "@mui/material/styles";
import {
  Box,
  CssBaseline,
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer as MuiDrawer,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Avatar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import SummarizeIcon from "@mui/icons-material/Summarize";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";

// New icon imports for related navigation items.
import HomeIcon from "@mui/icons-material/Home";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CategoryIcon from "@mui/icons-material/Category";
import StraightenIcon from "@mui/icons-material/Straighten";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PeopleIcon from "@mui/icons-material/People";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import FormatTextdirectionRToLIcon from "@mui/icons-material/FormatTextdirectionRToL";
import FormatTextdirectionLToRIcon from "@mui/icons-material/FormatTextdirectionLToR";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useRTL, RTLProvider } from "../context/RTLContext";
import LanguageSelector from "../lang/LanguageSelector";
import { hasTabAccess, canAccessPath } from "./constants/tabKeys";

const drawerWidth = 240;
const shopPrimaryGreen = "#355d31";
const shopPrimaryGreenDark = "#274923";
const shopPrimaryGreenLight = "#5b8a42";

// Mixins for the mini variant drawer
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const drawerPaperSurface = (theme) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#101010" : theme.palette.background.paper,
  color: theme.palette.mode === "dark" ? theme.palette.grey[100] : theme.palette.text.primary,
  borderRight:
    theme.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.06)" : `1px solid ${theme.palette.divider}`,
  backgroundImage:
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg, rgba(16,16,16,1) 0%, rgba(12,12,12,1) 100%)"
      : "none",
});

// Styled component for the drawer header
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: theme.direction === "rtl" ? "flex-start" : "flex-end",
  padding: theme.spacing(0, 1),
  color: theme.palette.mode === "dark" ? theme.palette.grey[100] : theme.palette.text.primary,
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg, rgba(24,24,24,0.98) 0%, rgba(18,18,18,0.98) 100%)"
      : theme.palette.background.paper,
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isRTL",
})(({ theme, open, isRTL }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundColor: theme.palette.mode === "dark" ? "#303030" : theme.palette.primary.main,
  color: theme.palette.mode === "dark" ? theme.palette.grey[100] : theme.palette.common.white,
  boxShadow: theme.palette.mode === "dark" ? "0 1px 0 rgba(255,255,255,0.05)" : undefined,
  borderBottom: theme.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.05)" : undefined,
  ...(open && {
    marginLeft: isRTL ? 0 : drawerWidth,
    marginRight: isRTL ? drawerWidth : 0,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      ...drawerPaperSurface(theme),
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      ...drawerPaperSurface(theme),
    },
  }),
}));

function Dashboard({ darkMode, setDarkMode }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme?.palette?.mode === "dark";
  const primaryMain = theme?.palette?.primary?.main || shopPrimaryGreen;
  const primaryLight = theme?.palette?.primary?.light || shopPrimaryGreenLight;
  const primaryDark = theme?.palette?.primary?.dark || shopPrimaryGreenDark;
  const paperBg = theme?.palette?.background?.paper || (isDarkMode ? "#1f1f1f" : "#fff");
  const defaultBg = theme?.palette?.background?.default || (isDarkMode ? "#121212" : "#fafafa");
  const textPrimary = theme?.palette?.text?.primary || (isDarkMode ? "#f5f5f5" : "#111827");
  const textSecondary = theme?.palette?.text?.secondary || (isDarkMode ? "rgba(255,255,255,0.7)" : "#6b7280");
  const { direction, isRTL, toggleDirection } = useRTL();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [activeGroup, setActiveGroup] = useState("main");
  const [expandedGroups, setExpandedGroups] = useState({
    main: true,
    management: true,
    inventory: true,
    transactions: true,
    payments: true,
    accounts: true,
    settings: true,
    finance: true,
    reports: true,
  });
  const [scrolling, setScrolling] = useState(false);
  const location = useLocation();

  const auth = useAuth();

  const currentUser = auth?.user?.user ?? auth?.user;

  // Redirect to dashboard on login or when path not allowed
  useEffect(() => {
    if (location.pathname === "/shop" || location.pathname === "/shop/") {
      navigate("/shop/dashboard", { replace: true });
      return;
    }
    if (
      currentUser &&
      !currentUser.is_superuser &&
      !canAccessPath(currentUser, location.pathname)
    ) {
      navigate("/shop/dashboard", { replace: true });
    }
  }, [location.pathname, navigate, currentUser]);

  // Handle scroll animation
  useEffect(() => {
    const mainContent = document.querySelector("[data-main-content]");
    if (mainContent) {
      const handleScroll = () => {
        setScrolling(true);
        clearTimeout(window.scrollTimeout);
        window.scrollTimeout = setTimeout(() => {
          setScrolling(false);
        }, 150);
      };

      mainContent.addEventListener("scroll", handleScroll);
      return () => {
        mainContent.removeEventListener("scroll", handleScroll);
        clearTimeout(window.scrollTimeout);
      };
    }
  }, []);

  // Update active group based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/dashboard")) setActiveGroup("main");
    else if (path.includes("/shop")) setActiveGroup("main");
    else if (
      path.includes("/employees") ||
      path.includes("/agents") ||
      path.includes("/agent-payments") ||
      path.includes("/customers") ||
      path.includes("/suppliers")
    )
      setActiveGroup("management");
    else if (
      path.includes("/category") ||
      path.includes("/units") ||
      path.includes("/products") ||
      path.includes("/stock")
    )
      setActiveGroup("inventory");
    else if (
      path.includes("/purchases") ||
      path.includes("/sales") ||
      path.includes("/sale-return") ||
      path.includes("/purchase-return")
    )
      setActiveGroup("transactions");
    else if (
      path.includes("/purchase-payments") ||
      path.includes("/sales-payments") ||
      path.includes("/supplier-payments")
    )
      setActiveGroup("finance");
    else if (
      path.includes("/accounts") ||
      path.includes("/account-transactions") ||
      path.includes("/account-transfers") ||
      path.includes("/passive-income")
    )
      setActiveGroup("accounts");
    else if (
      path.includes("/currencies") ||
      path.includes("/exchange-rates") ||
      path.includes("/location-settings")
    )
      setActiveGroup("settings");
    else if (
      path.includes("/expenses") ||
      path.includes("/expense-categories") ||
      path.includes("/payroll") ||
      path.includes("/advance")
    )
      setActiveGroup("finance");
    else if (path.includes("/report")) setActiveGroup("reports");
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const showAllTabs = currentUser?.is_superuser;

  // Define navigation groups with related items (each item has tabKey for permission)
  const navigationGroups = [
    {
      key: "main",
      title: t("sidebar.groups.main", "Main"),
      items: [
        {
          text: t("sidebar.dashboard"),
          icon: <DashboardOutlinedIcon sx={{ color: "#6fa263" }} />,
          path: "/shop/dashboard",
          tabKey: "dashboard",
        },

           {
          text: t("sidebar.users", "Users"),
          icon: <PeopleIcon />,
          path: "/shop/users",
          tabKey: "users",
        },
      ],
    },
    {
      key: "management",
      title: t("sidebar.groups.management", "Management"),
      items: [
        {
          text: t("sidebar.agents", "Agent"),
          icon: <PeopleIcon />,
          path: "/shop/agents",
          tabKey: "customers",
        },
        {
          text: t("sidebar.payments", "Payments"),
          icon: <PaymentIcon />,
          path: "/shop/agent-payments",
          tabKey: "customers",
        },
      ],
    },
    {
      key: "inventory",
      title: t("sidebar.groups.inventory", "Inventory"),
      items: [
        // {
        //   text: t("sidebar.categories"),
        //   icon: <CategoryIcon />,
        //   path: "/shop/category",
        //   tabKey: "category",
        // },
        {
          text: t("sidebar.units"),
          icon: <StraightenIcon />,
          path: "/shop/units",
          tabKey: "units",
        },
        // {
        //   text: t("sidebar.products"),
        //   icon: <Inventory2Icon />,
        //   path: "/shop/products",
        //   tabKey: "products",
        // },
        // {
        //   text: t("sidebar.stock"),
//   icon: <WarehouseIcon />,
        //   path: "/shop/stock",
        //   tabKey: "stock",
        // },
        // {
        //   text: t("sidebar.stockTransfer", "Stock Transfer"),
        //   icon: <MoveToInboxIcon />,
        //   path: "/shop/stock-transfer",
        //   tabKey: "stock_transfer",
        // },
        ],
      },
    {
      key: "accounts",
      title: t("sidebar.groups.accounts", "Accounts"),
      items: [
        {
          text: t("sidebar.accounts"),
          icon: <AccountBalanceIcon />,
          path: "/shop/accounts",
          tabKey: "accounts",
        },
        // {
        //   text: t("sidebar.accountTransactions"),
        //   icon: <ReceiptLongIcon />,
        //   path: "/shop/account-transactions",
        //   tabKey: "accounts",
        // },
        {
          text: t("sidebar.accountTransfers", "Account Transfers"),
          icon: <CurrencyExchangeIcon />,
          path: "/shop/account-transfers",
          tabKey: "accounts",
        },
        // {
        //   text: t("sidebar.passiveIncome", "Passive Income"),
        //   icon: <MonetizationOnIcon />,
        //   path: "/shop/passive-income",
        //   tabKey: "passive_income",
        // },
      ],
    },
    // {
    //   key: "settings",
    //   title: t("sidebar.groups.settings", "Settings"),
    //   items: [
    //     {
    //       text: t("sidebar.currencies"),
    //       icon: <AttachMoneyIcon />,
    //       path: "/shop/currencies",
    //       tabKey: "currencies",
    //     },
    //     {
    //       text: t("sidebar.exchangeRates"),
    //       icon: <CurrencyExchangeIcon />,
    //       path: "/shop/exchange-rates",
    //       tabKey: "exchange_rates",
    //     },
    //     // {
    //     //   text: t("sidebar.locationSettings", "Location Settings"),
    //     //   icon: <HomeIcon />,
    //     //   path: "/shop/location-settings",
    //     //   tabKey: "location_settings",
    //     // },
    //   ],
    // },
    // {
    //   key: "finance",
    //   title: t("sidebar.groups.finance", "Finance"),
    //   items: [
    //     // {
    //     //   text: t("sidebar.payroll"),
    //     //   icon: <AccountBalanceWalletIcon />,
    //     //   path: "/shop/payroll",
    //     //   tabKey: "payroll",
    //     // },
    //     // {
    //     //   text: t("sidebar.advance"),
    //     //   icon: <MonetizationOnIcon />,
    //     //   path: "/shop/advance",
    //     //   tabKey: "advance",
    //     // },
    //     {
    //       text: t("sidebar.expenses"),
    //       icon: <ReceiptIcon />,
    //       path: "/shop/expenses",
    //       tabKey: "expenses",
    //     },
    //     {
    //       text: t("sidebar.expenseCategories"),
    //       icon: <CategoryIcon />,
    //       path: "/shop/expense-categories",
    //       tabKey: "expense_categories",
    //     },
    //   ],
    // },
    {
      key: "reports",
      title: t("sidebar.groups.reports", "Reports"),
      items: [
        {
          text: t("sidebar.reports"),
          icon: <SummarizeIcon />,
          path: "/shop/report-index",
          tabKey: "reports",
        },
      ],
    },
  ];

  const filteredNavigationGroups = showAllTabs
    ? navigationGroups
    : navigationGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) =>
            hasTabAccess(currentUser, item.tabKey)
          ),
        }))
        .filter((group) => group.items.length > 0);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} isRTL={isRTL}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge={isRTL ? "end" : "start"}
            sx={isRTL ? { ml: 2 } : { mr: 2 }}
          >
            {open ? (
              isRTL ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )
            ) : (
              <MenuIcon />
            )}
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            {t("sidebar.dashboardTitle", "Professional Dashboard")}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LanguageSelector />

            {/* <Tooltip title={isRTL ? t('rtl.switchToLTR', 'Switch to LTR') : t('rtl.switchToRTL', 'Switch to RTL')}>
              <IconButton
                color="inherit"
                onClick={toggleDirection}
                sx={{ mx: 1 }}
              >
                {isRTL ? <FormatTextdirectionLToRIcon /> : <FormatTextdirectionRToLIcon />}
              </IconButton>
            </Tooltip> */}
          </Box>

          <IconButton
            color="inherit"
            onClick={() => setDarkMode(!darkMode)}
            sx={isRTL ? { ml: 2 } : { mr: 2 }}
          >
            {theme.palette.mode === "dark" ? (
              <Brightness7Icon />
            ) : (
              <Brightness4Icon />
            )}
          </IconButton>
          {/* <IconButton
            color="inherit"
            sx={theme.direction === "rtl" ? { ml: 2 } : { mr: 2 }}
          >
            <Badge badgeContent={4} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton> */}
          <Tooltip title={t("sidebar.accountSettings", "Account settings")}>
            <IconButton onClick={handleProfileMenuOpen} color="inherit">
              <Avatar alt="User Name" src="/logo.jpg" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={open}
        anchor={isRTL ? "right" : "left"}
        sx={{
          "& .MuiDrawer-paper": {
            direction: isRTL ? "rtl" : "ltr",
            overflowX: "hidden",
          },
        }}
      >
        <DrawerHeader>
          <Typography
            variant="h6"
            noWrap
            sx={{
              flexGrow: 1,
              pl: isRTL ? 0 : 2,
              pr: isRTL ? 2 : 0,
            }}
          >
            {t("sidebar.menu", "Menu")}
          </Typography>
          <IconButton onClick={handleDrawerToggle}>
            {open ? (
              isRTL ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )
            ) : (
              <MenuIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List
          sx={{
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: `${primaryMain}40`,
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: `${primaryMain}60`,
            },
            scrollbarWidth: "thin",
            scrollbarColor: `${primaryMain}40 transparent`,
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          {filteredNavigationGroups.map((group, groupIndex) => {
            const groupKey = group.key;
            return (
              <Box key={groupKey}>
                {open && (
                  <ListSubheader
                    sx={{
                      textAlign: isRTL ? "right" : "left",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color:
                        activeGroup === groupKey
                          ? primaryMain
                          : textSecondary,
                      backgroundColor: scrolling
                        ? `${primaryMain}25`
                        : activeGroup === groupKey
                        ? `${primaryMain}15`
                        : "transparent",
                      lineHeight: "32px",
                      px: 2,
                      py: 1,
                      mx: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      border:
                        activeGroup === groupKey
                          ? `1px solid ${primaryMain}30`
                          : "1px solid transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transition: "all 0.3s ease",
                      transform: scrolling ? "translateY(-1px)" : "translateY(0)",
                      boxShadow: scrolling
                        ? `0 2px 8px ${primaryMain}20`
                        : "none",
                      "&:hover": {
                        backgroundColor: `${primaryMain}20`,
                        color: primaryMain,
                      },
                    }}
                    onClick={() => {
                      setActiveGroup(groupKey);
                    }}
                  >
                    <span>{group.title}</span>
                  </ListSubheader>
                )}
                {expandedGroups[groupKey] &&
                  group.items.map((item) => (
                    <ListItem
                      button
                      key={item.text}
                      component={Link}
                      to={item.path}
                      sx={{
                        padding: open ? theme.spacing(1, 2) : theme.spacing(1, 1),
                        justifyContent: open ? "initial" : "center",
                        direction: isRTL ? "rtl" : "ltr",
                        mx: 1,
                        borderRadius: 2,
                        backgroundColor:
                          activeGroup === groupKey
                            ? `${primaryMain}08`
                            : "transparent",
                        "&:hover": {
                          backgroundColor: `${primaryMain}15`,
                          "& .MuiListItemIcon-root": {
                            color: primaryMain,
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isRTL ? 0 : open ? 2 : "auto",
                          ml: isRTL ? (open ? 2 : "auto") : 0,
                          justifyContent: "center",
                          color: primaryMain,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {open && (
                        <ListItemText
                          primary={item.text}
                          sx={{
                            textAlign: isRTL ? "right" : "left",
                            "& .MuiListItemText-primary": {
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              color: textPrimary,
                            },
                          }}
                        />
                      )}
                    </ListItem>
                  ))}
                {groupIndex < navigationGroups.length - 1 && open && (
                  <Divider sx={{ my: 1 }} />
                )}
              </Box>
            );
          })}
        </List>
      </Drawer>
          <Box
        component="main"
        data-main-content
        sx={{
          flexGrow: 1,
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          direction: isRTL ? "rtl" : "ltr",
          backgroundColor: defaultBg,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: isDarkMode ? "#0f0f0f" : (theme?.palette?.grey?.[100] || "#f3f4f6"),
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: `${primaryMain}60`,
            borderRadius: "4px",
            transition: "background-color 0.3s ease",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: `${primaryMain}80`,
          },
          scrollbarWidth: "thin",
          scrollbarColor: `${primaryMain}60 ${theme?.palette?.grey?.[100] || "#f3f4f6"}`,
        }}
      >
        <DrawerHeader />
        <Outlet />
      </Box>
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleProfileMenuClose}>
          <Link
            onClick={() => {
              auth.logout();
            }}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {t("sidebar.auth.logout", "Logout")}
          </Link>
        </MenuItem>
      </Menu>
    </Box>
  );
}

function DashboardWithRTL({ darkMode, setDarkMode }) {
  const { direction } = useRTL();

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: shopPrimaryGreen,
            dark: shopPrimaryGreenDark,
            light: shopPrimaryGreenLight,
          },
          secondary: { main: "#dc004e" },
          ...(darkMode
            ? {
                background: {
                  default: "#121212",
                  paper: "#1f1f1f",
                },
                text: {
                  primary: "#f5f5f5",
                  secondary: "rgba(255,255,255,0.7)",
                },
              }
            : {}),
        },
        direction: direction,
        typography: {
          fontFamily:
            direction === "rtl"
              ? '"Roboto", "Helvetica", "Arial", "Tahoma", sans-serif'
              : '"Roboto", "Helvetica", "Arial", sans-serif',
        },
        components: {
          MuiDrawer: {
            styleOverrides: {
              paper: {
                direction: direction,
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                direction: direction,
              },
            },
          },
        },
      }),
    [darkMode, direction]
  );

  return (
    <ThemeProvider theme={theme}>
      <Box dir={direction} sx={{ direction }}>
        <Dashboard darkMode={darkMode} setDarkMode={setDarkMode} />
      </Box>
    </ThemeProvider>
  );
}

export default function DashboardWrapper() {
  const [darkMode, setDarkMode] = useState(false);

  return (
    <RTLProvider>
      <DashboardWithRTL darkMode={darkMode} setDarkMode={setDarkMode} />
    </RTLProvider>
  );
}
